<template>
	<div class="sticky w-full top-full">
		<UFooter
			:ui="{
				container: 'hidden lg:hidden',
				root: [
					'transform transition-[height] duration-300 ease-in-out',
					collapsed ? 'sm:h-auto overflow-clip' : 'h-auto',
					locale === 'en' && collapsed ? 'h-68' : '',
					locale === 'es' && collapsed ? 'h-72' : ''
				]
			}"
		>
			<template #top>
				<UContainer>
					<div class="relative">
						<div class="flex items-center justify-between flex-row gap-4">
							<UButton
								:id="FOOTER_LOGO_BUTTON_ID"
								:to="localePath('/')"
								aria-label="Maverix Broadband"
								color="secondary"
								variant="ghost"
								:ui="{ base: '-ml-2.5' }"
								@click="trackEvent($event, FOOTER_NAV_LOCATION_ID)"
								@mouseenter="trackEvent($event, FOOTER_NAV_LOCATION_ID)"
								@focus="trackEvent($event, FOOTER_NAV_LOCATION_ID)"
							>
								<LogoFull class="w-auto h-10 sm:h-11 shrink-0 text-inverted" />
							</UButton>
							<UButton
								v-if="loggedIn"
								:id="PAY_BILL_BUTTON_ID"
								:label="$t('footer.pay-bill')"
								target="_blank"
								:to="accountUrl"
								color="neutral"
								variant="outline"
								:aria-label="$t('footer.pay-bill')"
								size="xl"
								@click="trackEvent($event, FOOTER_NAV_LOCATION_ID)"
								@mouseenter="trackEvent($event, FOOTER_NAV_LOCATION_ID)"
								@focus="trackEvent($event, FOOTER_NAV_LOCATION_ID)"
							/>
						</div>
						<UButton
							:id="FOOTER_TOGGLE_BUTTON_ID"
							:icon="collapsed ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'"
							:aria-label="collapsed ? $t('footer.expand') : $t('footer.collapse')"
							class="absolute -top-8 left-1/2 transform -translate-x-1/2 sm:hidden z-10"
							color="secondary"
							variant="ghost"
							@click="toggleFooter"
							@click.prevent="trackEvent($event, MOBILE_FOOTER_NAV_LOCATION_ID, {
								action: collapsed ? 'expand' : 'collapse'
							})"
						/>
						<UFooterColumns
							:columns="columns"
							class="xl:justify-end"
						>
							<template #left>
								<div class="flex flex-col gap-4">
									<div class="flex items-center gap-2">
										<UButton
											:id="GOOGLE_MAPS_BUTTON_ID"
											to="https://www.google.com/maps/place/Maverix+Broadband/@39.4997395,-104.710539,11z/data=!4m6!3m5!1s0x6efa953aab4d9d79:0xf16f6e005df2538!8m2!3d39.4997395!4d-104.710539!16s%2Fg%2F11sxyfbj_4?sa=X&ved=2ahUKEwiHhJfJkIT-AhVCOH0KHfGbBMYQ_BJ6BQiqARAI&coh=164777&entry=tt&shorturl=1"
											target="_blank"
											icon="i-simple-icons-google"
											aria-label="Google"
											color="secondary"
											variant="ghost"
											@click="trackEvent($event, FOOTER_NAV_LOCATION_ID)"
											@mouseenter="trackEvent($event, FOOTER_NAV_LOCATION_ID)"
											@focus="trackEvent($event, FOOTER_NAV_LOCATION_ID)"
										/>
										<UButton
											:id="NEXTDOOR_BUTTON_ID"
											to="https://nextdoor.com/pages/maverix-broadband-parker-co/"
											target="_blank"
											icon="i-custom-nextdoor"
											aria-label="Nextdoor"
											color="secondary"
											variant="ghost"
											@click="trackEvent($event, FOOTER_NAV_LOCATION_ID)"
											@mouseenter="trackEvent($event, FOOTER_NAV_LOCATION_ID)"
											@focus="trackEvent($event, FOOTER_NAV_LOCATION_ID)"
										/>
										<UButton
											:id="FACEBOOK_BUTTON_ID"
											to="https://www.facebook.com/maverixBB/"
											target="_blank"
											icon="i-simple-icons-facebook"
											aria-label="Facebook"
											color="secondary"
											variant="ghost"
											@click="trackEvent($event, FOOTER_NAV_LOCATION_ID)"
											@mouseenter="trackEvent($event, FOOTER_NAV_LOCATION_ID)"
											@focus="trackEvent($event, FOOTER_NAV_LOCATION_ID)"
										/>
									</div>

									<p class="text-sm max-w-xs">
										{{ $t('footer.providing') }}
									</p>

									<p class="text-sm text-inverted">
										{{ $t('footer.copyright') }} © Maverix Broadband, Inc. {{ new Date().getFullYear() }}.
										<NuxtLink
											:to="localePath('/tos')"
											class="underline underline-offset-2 decoration hover:decoration-2"
										>
											{{ $t('footer.tos') }}
										</NuxtLink>
										{{ $t('footer.and') }}
										<NuxtLink
											:to="localePath('/privacy')"
											class="underline underline-offset-2 decoration hover:decoration-2"
										>
											{{ $t('footer.privacy-policy') }}
										</NuxtLink>
									</p>
								</div>
							</template>
						</UFooterColumns>
					</div>
				</UContainer>
			</template>
		</UFooter>
	</div>
</template>

<script setup lang="ts">
import { useAuth } from '@/composables/useAuth'
import { useGA4Tracking } from '@/composables/useGA4Tracking'
import { useNavigationTracking } from '@/composables/useNavigationTracking'
import {
	FACEBOOK_BUTTON_ID,
	FOOTER_LOGO_BUTTON_ID,
	FOOTER_NAV_LOCATION_ID,
	FOOTER_TOGGLE_BUTTON_ID,
	GOOGLE_MAPS_BUTTON_ID,
	MOBILE_FOOTER_NAV_LOCATION_ID,
	NEXTDOOR_BUTTON_ID,
	PAY_BILL_BUTTON_ID
} from '@/constants/ga4/tracking-ids/layout/footer'
import { HELP_CENTER_BASE_URL_EN } from '@/constants/index.js'
import { useI18n } from 'vue-i18n'

const localePath = useLocalePath()
const {
	loggedIn, accountUrl
} = useAuth()

const { trackEvent } = useGA4Tracking()
const { setupFooterNavigationTracking } = useNavigationTracking()

const {
	t, locale
} = useI18n({
	useScope: 'global'
})

const collapsed = ref(true)

const columns = computed(() => [
	{
		label: t('links.services'),
		children: [
			{
				label: t('links.residential'),
				to: localePath('/residential-internet')
			},
			{
				label: t('links.business'),
				to: localePath('/business-internet')
			},
			{
				label: t('links.service-areas'),
				to: localePath('/service-areas')
			},
			{
				label: t('links.installation'),
				to: localePath('/installation-overview')
			}
		]
	},
	{
		label: t('links.construction'),
		children: [
			{
				label: t('links.installation'),
				to: localePath('/installation-overview')
			},
			{
				label: t('links.eight-one-one'),
				to: localePath('/811')
			}
		]
	},
	{
		label: t('links.about'),
		children: [
			{
				label: t('links.about-us'),
				to: localePath('/about')
			},
			{
				label: t('links.blog'),
				to: localePath('/blog')
			},
			{
				label: t('links.fiber-news'),
				to: localePath('/press')
			},
			{
				label: t('links.careers'),
				to: localePath('/careers')
			}
		]
	},
	{
		label: t('links.support'),
		children: [
			...(loggedIn.value
				? [
					{
						label: t('links.your-account'), // only show if logged in
						to: accountUrl.value,
						target: '_blank',
						external: true
					}
				]
				: []),
			{
				label: t('links.help-center'),
				to: HELP_CENTER_BASE_URL_EN,
				target: '_blank',
				external: true
			},
			{
				label: t('links.contact-us'),
				to: localePath('/contact')
			}
		]
	}
])

const toggleFooter = () => {
	collapsed.value = !collapsed.value
}

watch(collapsed, newValue => {
	if (import.meta.client && !newValue) {
		// scroll to the bottom of the page when expanded with short delay
		setTimeout(() => {
			window?.scrollTo({
				top: document.body.scrollHeight,
				behavior: 'smooth'
			})
		}, 50)
	}
}, { immediate: true })

onMounted(() => {
	// Setup navigation tracking once DOM is ready
	nextTick(() => {
		setupFooterNavigationTracking(columns.value)
	})
})
</script>
